import { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { CalendarPlus, Eye, ListChecks } from '@phosphor-icons/react';
import AppointmentCard from './AppointmentCard';
import CurrentTimeLine from './CurrentTimeLine';
import { useSelector } from 'react-redux';
import { selectClinicProfile } from '../../store/slices/authSlice';
import { buildCalendarHours, getClinicCalendarStartHour } from '../../utils/calendarHours';
import { useCalendarColorMode, matchesConfirmationFilter } from '../../utils/appointmentDisplay';
import { formatLocalDateString, HEBREW_DAYS_SHORT, HEBREW_DAYS_FULL } from '../../utils/dateUtils';
import { getHolidayForDate, getHolidaySlotSeverity } from '../../utils/israeliHolidays';
import { AbsenceBands, AbsenceHeaderBadge } from './AbsenceBands';
import './WeekView.css';

const LONG_PRESS_MS = 500;

// Compute per-appointment { col, totalCols } using CLUSTER-LOCAL totalCols.
//
// A "cluster" is a maximal run of appointments connected by overlap (possibly
// transitively — A overlaps B, B overlaps C ⇒ {A,B,C} is one cluster). Every
// appointment within a cluster shares the same `totalCols` (= max columns
// simultaneously used inside that cluster), but appointments in different
// clusters each get their own `totalCols`.
//
// Why: with a single day-wide `totalCols`, a brief 3-way overlap at 10:00
// would squish every unrelated appointment later in the day to 1/3 width.
// Clustering keeps lone / pair appointments at full (or 1/2) width.
export function getOverlapLayout(appointments) {
  if (!appointments.length) return {};
  const sorted = [...appointments].sort((a, b) => {
    const aStart = new Date(a.startTime).getTime();
    const bStart = new Date(b.startTime).getTime();
    return aStart !== bStart ? aStart - bStart : new Date(b.endTime).getTime() - new Date(a.endTime).getTime();
  });

  const result = {};
  let clusterApts = [];     // [{ id, col }] — members of the in-flight cluster
  let clusterCols = [];     // per-column end times within the current cluster
  let clusterEnd = 0;       // max endTime seen in the current cluster

  const flushCluster = () => {
    const totalCols = clusterCols.length;
    clusterApts.forEach(({ id, col }) => {
      result[id] = { col, totalCols };
    });
    clusterApts = [];
    clusterCols = [];
    clusterEnd = 0;
  };

  sorted.forEach((apt) => {
    const aptStart = new Date(apt.startTime).getTime();
    const aptEnd = new Date(apt.endTime).getTime();

    // No overlap with any active apt in the cluster → flush & start a new one.
    if (clusterApts.length > 0 && aptStart >= clusterEnd) {
      flushCluster();
    }

    let placed = false;
    for (let col = 0; col < clusterCols.length; col++) {
      if (aptStart >= clusterCols[col]) {
        clusterCols[col] = aptEnd;
        clusterApts.push({ id: apt.id, col });
        placed = true;
        break;
      }
    }
    if (!placed) {
      clusterApts.push({ id: apt.id, col: clusterCols.length });
      clusterCols.push(aptEnd);
    }

    if (aptEnd > clusterEnd) clusterEnd = aptEnd;
  });

  if (clusterApts.length > 0) flushCluster();

  return result;
}

const getSlotTooltip = (date, hour) => {
  const dayName = HEBREW_DAYS_FULL[date.getDay()];
  const dateFormatted = date.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
  return `יום ${dayName}, ${dateFormatted} | ${hour.toString().padStart(2, '0')}:00`;
};

function WeekView({
  startDate,
  appointments,
  availabilitySlots = [],
  onSlotClick,
  onAppointmentClick,
  onAppointmentContextMenu,
  onViewDay,
  onAddTask,
  onDragDrop,
  showTherapistName = false,
  multilineTherapist = false,
  therapistColorMap = null,
  therapistAvailabilities = [],
  roomAvailability = null, // [{date, mode: 'open'|'slots'|'closed'|'holiday', slots}]
  singleDayMode = false,
  selectedDayIndex = 0,
  confirmationFilter = 'all',
  absences = [], // multi-clinic v2: [{therapistId, therapistName, startDate, endDate, allDay, startTime, endTime, type, canSeeDetails}]
}) {
  const [contextMenu, setContextMenu] = useState(null);
  const contextMenuRef = useRef(null);
  const [dropIndicator, setDropIndicator] = useState(null);
  const [draggedAptId, setDraggedAptId] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
  const touchRef = useRef({ timer: null, ghost: null, isDragging: false, apt: null, card: null, startX: 0, startY: 0, indicator: null });

  // Clinic-configurable grid start (clinic.calendarStartHour, default 05:00) → rows up to 23:00
  const clinicProfile = useSelector(selectClinicProfile);
  const FIRST_HOUR = getClinicCalendarStartHour(clinicProfile);
  const HOURS = useMemo(() => buildCalendarHours(FIRST_HOUR), [FIRST_HOUR]);
  // Clinic color mode (status vs treatment type) — one subscription per view.
  const { mode: colorMode, treatmentTypeMap } = useCalendarColorMode();

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) closeContextMenu();
    };
    const handleKey = (e) => { if (e.key === 'Escape') closeContextMenu(); };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    document.addEventListener('scroll', closeContextMenu, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('scroll', closeContextMenu, true);
    };
  }, [contextMenu, closeContextMenu]);

  useLayoutEffect(() => {
    if (!contextMenu || !contextMenuRef.current) return;
    const rect = contextMenuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let x = contextMenu.x;
    let y = contextMenu.y;
    if (x + rect.width > vw - 8) x = vw - rect.width - 8;
    if (y + rect.height > vh - 8) y = vh - rect.height - 8;
    contextMenuRef.current.style.left = `${x}px`;
    contextMenuRef.current.style.top = `${y}px`;
  }, [contextMenu]);

  const handleContextMenu = (e, date, hour) => {
    if (e.target.closest('.appointment-card')) return;
    e.preventDefault();
    const slotDate = new Date(date);
    slotDate.setHours(hour, 0, 0, 0);
    setContextMenu({ x: e.clientX, y: e.clientY, slotDate, dayDate: date, hour });
  };

  const handleMenuAction = (action) => {
    const { slotDate, dayDate } = contextMenu || {};
    closeContextMenu();
    if (!slotDate) return;
    if (action === 'create-appointment') onSlotClick(slotDate);
    else if (action === 'view-day') onViewDay(dayDate);
    else if (action === 'add-task') onAddTask(dayDate);
  };

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      return d;
    });
  }, [startDate]);

  const displayDays = useMemo(() => {
    if (singleDayMode) return [weekDays[selectedDayIndex]];
    return weekDays;
  }, [singleDayMode, selectedDayIndex, weekDays]);

  const availabilityByDate = useMemo(() => {
    const result = {};
    availabilitySlots.forEach((slot) => { result[slot.date] = slot.slots || []; });
    return result;
  }, [availabilitySlots]);

  const blockedByDate = useMemo(() => {
    const result = {};
    availabilitySlots.forEach((slot) => { result[slot.date] = slot.blocked || []; });
    return result;
  }, [availabilitySlots]);

  const isAvailableHour = (date, hour) => {
    const dateStr = formatLocalDateString(date);
    const daySlots = availabilityByDate[dateStr];
    if (!daySlots || !daySlots.length) return false;
    return daySlots.some((slot) => {
      const startHour = parseInt(slot.start.split(':')[0], 10);
      const endHour = parseInt(slot.end.split(':')[0], 10);
      return hour >= startHour && hour < endHour;
    });
  };

  const roomAvailabilityByDate = useMemo(() => {
    if (!roomAvailability) return null;
    const result = {};
    roomAvailability.forEach((d) => { result[d.date] = d; });
    return result;
  }, [roomAvailability]);

  // 'open' | 'closed' | null (no overlay) for the selected room at this hour
  const getRoomHourState = (date, hour) => {
    if (!roomAvailabilityByDate) return null;
    const day = roomAvailabilityByDate[formatLocalDateString(date)];
    if (!day) return null;
    if (day.mode === 'open') return 'open';
    if (day.mode === 'closed' || day.mode === 'holiday') return 'closed';
    const within = (day.slots || []).some((slot) => {
      const sh = parseInt(slot.start.split(':')[0], 10);
      const eh = parseInt(slot.end.split(':')[0], 10);
      return hour >= sh && hour < eh;
    });
    return within ? 'open' : 'closed';
  };

  const isBlockedHour = (date, hour) => {
    const dateStr = formatLocalDateString(date);
    const dayBlocked = blockedByDate[dateStr];
    if (!dayBlocked || !dayBlocked.length) return false;
    return dayBlocked.some((slot) => {
      const startHour = parseInt(slot.start.split(':')[0], 10);
      const endHour = parseInt(slot.end.split(':')[0], 10);
      return hour >= startHour && hour < endHour;
    });
  };

  const getTherapistsAvailableAtHour = (date, hour) => {
    if (!therapistAvailabilities || therapistAvailabilities.length === 0) return [];
    const dateStr = formatLocalDateString(date);
    const available = [];
    therapistAvailabilities.forEach((ta) => {
      const daySlot = ta.slots.find((s) => s.date === dateStr);
      if (daySlot?.slots?.length > 0) {
        const isAvail = daySlot.slots.some((slot) => {
          const sh = parseInt(slot.start.split(':')[0], 10);
          const eh = parseInt(slot.end.split(':')[0], 10);
          return hour >= sh && hour < eh;
        });
        if (isAvail) available.push({ therapistId: ta.therapistId, color: ta.color, name: ta.therapistName });
      }
    });
    return available;
  };

  // Slot height must match the CSS values in WeekView.css for perfect grid alignment.
  // Single-day mode: 60px (all breakpoints). Week mode: 50px desktop / 40px mobile (<=768px).
  // When the parent requests a 2-line layout (e.g. ClinicCalendarModal), we bump
  // the slot height so 30-minute appointments still have room for both therapist
  // and patient names. The CSS uses a matching `.week-view.tall-slots` selector.
  const slotHeight = multilineTherapist
    ? (singleDayMode ? 72 : isMobile ? 56 : 64)
    : (singleDayMode ? 100 : isMobile ? 69 : 86);

  const positionedAppointmentsByDay = useMemo(() => {
    const totalGridHeight = HOURS.length * slotHeight;
    const result = {};
    weekDays.forEach((dayDate, dayIndex) => {
      const dayAppts = appointments.filter(apt => {
        const ad = new Date(apt.startTime);
        return ad.getDate() === dayDate.getDate() && ad.getMonth() === dayDate.getMonth() && ad.getFullYear() === dayDate.getFullYear();
      });
      const layout = getOverlapLayout(dayAppts);
      result[dayIndex] = dayAppts.map(apt => {
        const start = new Date(apt.startTime);
        const end = new Date(apt.endTime);
        const startMinutes = start.getHours() * 60 + start.getMinutes();
        const firstMinute = FIRST_HOUR * 60;
        const topPx = Math.max(0, ((startMinutes - firstMinute) / 60) * slotHeight);
        const durationMinutes = (end - start) / (1000 * 60);
        const heightPx = Math.min(Math.max((durationMinutes / 60) * slotHeight, 18), totalGridHeight - topPx);
        const overlap = layout[apt.id] || { col: 0, totalCols: 1 };
        return { ...apt, topPx, heightPx, overlap };
      });
    });
    return result;
  }, [appointments, weekDays, slotHeight, HOURS, FIRST_HOUR]);

  // Mobile-only week-view column collapse (column shrinks to 20% width).
  // Auto rules: a fully-past day collapses; empty Friday/Saturday collapse.
  // Tapping a day header toggles and overrides the auto rule for that day.
  const [manualCollapse, setManualCollapse] = useState({});
  const weekKey = formatLocalDateString(weekDays[0]);
  useEffect(() => { setManualCollapse({}); }, [weekKey]);

  // The "past day collapses" rule exists to give the CURRENT week's remaining
  // days more room. When the whole displayed week is already in the past
  // (browsing back), every column would collapse — the week renders as seven
  // equal-width strips with every patient name hidden. Skip the rule then.
  const weekIsFullyPast = useMemo(() => {
    const endOfWeek = new Date(weekDays[weekDays.length - 1]);
    endOfWeek.setHours(23, 59, 59, 999);
    return endOfWeek.getTime() < Date.now();
  }, [weekDays]);

  const isDayCollapsed = useCallback((dayIndex, dayDate) => {
    if (!isMobile || singleDayMode) return false;
    if (manualCollapse[dayIndex] !== undefined) return manualCollapse[dayIndex];
    if (!weekIsFullyPast) {
      const endOfDay = new Date(dayDate);
      endOfDay.setHours(23, 59, 59, 999);
      if (endOfDay.getTime() < Date.now()) return true;
    }
    const dow = dayDate.getDay();
    if ((dow === 5 || dow === 6) && (positionedAppointmentsByDay[dayIndex] || []).length === 0) return true;
    return false;
  }, [isMobile, singleDayMode, manualCollapse, weekIsFullyPast, positionedAppointmentsByDay]);

  const toggleDayCollapse = (dayIndex, dayDate) => {
    if (!isMobile || singleDayMode) return;
    setManualCollapse((prev) => ({ ...prev, [dayIndex]: !isDayCollapsed(dayIndex, dayDate) }));
  };

  // Auto-scroll once per displayed week to just above the earliest meeting
  // (browser clamps to the grid end if the meeting is late in the day).
  const bodyRef = useRef(null);
  const autoScrolledKeyRef = useRef(null);
  useEffect(() => {
    const key = formatLocalDateString(weekDays[0]);
    if (autoScrolledKeyRef.current === key) return;
    const tops = Object.values(positionedAppointmentsByDay)
      .flat()
      .map((apt) => apt.topPx);
    if (!tops.length) return;
    autoScrolledKeyRef.current = key;
    bodyRef.current?.scrollTo({
      top: Math.max(0, Math.min(...tops) - slotHeight),
      behavior: "smooth",
    });
  }, [positionedAppointmentsByDay, weekDays, slotHeight]);

  const handleSlotClick = (displayIndex, hour) => {
    const actualDayIndex = singleDayMode ? selectedDayIndex : displayIndex;
    const slotDate = new Date(weekDays[actualDayIndex]);
    slotDate.setHours(hour, 0, 0, 0);
    onSlotClick(slotDate);
  };

  const formatHour = (hour) => `${hour.toString().padStart(2, '0')}:00`;

  const isToday = (d) => {
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  };

  const isPastSlot = (d, hour) => {
    const now = new Date();
    const s = new Date(d);
    s.setHours(hour + 1, 0, 0, 0);
    return s < now;
  };

  const canDrag = (apt) => apt.status !== 'cancelled' && apt.status !== 'completed';

  // === HTML5 Drag & Drop (Desktop) ===
  const handleDragStart = (e, apt) => {
    if (!canDrag(apt)) { e.preventDefault(); return; }
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', apt.id);
    setDraggedAptId(apt.id);
    requestAnimationFrame(() => {
      e.target.closest('.positioned-appointment')?.classList.add('is-dragging');
    });
  };

  const handleDragEnd = () => {
    setDraggedAptId(null);
    setDropIndicator(null);
    document.querySelectorAll('.is-dragging').forEach(el => el.classList.remove('is-dragging'));
  };

  const handleSlotDragOver = (e, dateStr, hour) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const isBottomHalf = y > rect.height / 2;
    setDropIndicator({ dateStr, hour, isBottomHalf });
  };

  const handleSlotDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropIndicator(null);
  };

  const handleSlotDrop = (e, dayDate, hour) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const minute = y > rect.height / 2 ? 30 : 0;
    const apt = appointments.find(a => a.id === draggedAptId);
    if (apt && onDragDrop) onDragDrop(apt, dayDate, hour, minute);
    handleDragEnd();
  };

  // === Touch Drag (Mobile) ===
  const handleTouchStart = useCallback((e, apt) => {
    if (!canDrag(apt)) return;
    const ref = touchRef.current;
    const touch = e.touches[0];
    ref.startX = touch.clientX;
    ref.startY = touch.clientY;
    ref.apt = apt;
    ref.card = e.currentTarget;
    ref.timer = setTimeout(() => {
      ref.isDragging = true;
      setDraggedAptId(apt.id);
      const card = ref.card;
      const ghost = card.cloneNode(true);
      ghost.className = 'drag-ghost';
      ghost.style.cssText = `position:fixed;width:${card.offsetWidth}px;pointer-events:none;z-index:10000;opacity:0.85;transform:scale(1.03);left:${touch.clientX - card.offsetWidth / 2}px;top:${touch.clientY - 20}px;border-radius:6px;box-shadow:0 8px 30px rgba(0,0,0,0.2);`;
      document.body.appendChild(ghost);
      ref.ghost = ghost;
      card.classList.add('is-dragging');
      if (navigator.vibrate) navigator.vibrate(50);
    }, LONG_PRESS_MS);
  }, []);

  const handleGlobalTouchMove = useCallback((e) => {
    const ref = touchRef.current;
    if (!ref.isDragging) {
      if (ref.timer) {
        const t = e.touches[0];
        if (Math.abs(t.clientX - ref.startX) > 10 || Math.abs(t.clientY - ref.startY) > 10) {
          clearTimeout(ref.timer);
          ref.timer = null;
        }
      }
      return;
    }
    e.preventDefault();
    const touch = e.touches[0];
    if (ref.ghost) {
      ref.ghost.style.left = `${touch.clientX - ref.ghost.offsetWidth / 2}px`;
      ref.ghost.style.top = `${touch.clientY - 20}px`;
      ref.ghost.style.display = 'none';
    }
    const el = document.elementFromPoint(touch.clientX, touch.clientY);
    if (ref.ghost) ref.ghost.style.display = '';
    const slot = el?.closest('.hour-slot');
    if (slot?.dataset.hour && slot?.dataset.date) {
      const rect = slot.getBoundingClientRect();
      const y = touch.clientY - rect.top;
      const isBottomHalf = y > rect.height / 2;
      const hour = parseInt(slot.dataset.hour, 10);
      const dateStr = slot.dataset.date;
      ref.indicator = { dateStr, hour, isBottomHalf };
      setDropIndicator({ dateStr, hour, isBottomHalf });
    } else {
      ref.indicator = null;
      setDropIndicator(null);
    }
  }, []);

  const cleanupTouch = useCallback(() => {
    const ref = touchRef.current;
    clearTimeout(ref.timer);
    ref.timer = null;
    if (ref.isDragging) {
      const ind = ref.indicator;
      if (ind && ref.apt && onDragDrop) {
        const targetDate = new Date(ind.dateStr + 'T00:00:00');
        onDragDrop(ref.apt, targetDate, ind.hour, ind.isBottomHalf ? 30 : 0);
      }
      if (ref.ghost) { ref.ghost.remove(); ref.ghost = null; }
      if (ref.card) ref.card.classList.remove('is-dragging');
    }
    ref.isDragging = false;
    ref.apt = null;
    ref.card = null;
    ref.indicator = null;
    setDraggedAptId(null);
    setDropIndicator(null);
  }, [onDragDrop]);

  useEffect(() => {
    document.addEventListener('touchmove', handleGlobalTouchMove, { passive: false });
    document.addEventListener('touchend', cleanupTouch);
    document.addEventListener('touchcancel', cleanupTouch);
    return () => {
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('touchend', cleanupTouch);
      document.removeEventListener('touchcancel', cleanupTouch);
    };
  }, [handleGlobalTouchMove, cleanupTouch]);

  return (
    <div className={`week-view ${singleDayMode ? 'single-day-mode' : ''} ${multilineTherapist ? 'tall-slots' : ''}`}>
      <div className="week-view-header">
        <div className="header-time-spacer"></div>
        {displayDays.map((d, index) => {
          const holiday = getHolidayForDate(d);
          return (
            <div
              key={index}
              className={`day-header ${isToday(d) ? "today" : ""} ${isDayCollapsed(index, d) ? "collapsed" : ""}`}
              onClick={() => toggleDayCollapse(index, d)}
            >
              <span className="day-name">{HEBREW_DAYS_SHORT[d.getDay()]}'</span>
              <span className="day-date">{d.getDate()}</span>
              {singleDayMode && (
                <span className="day-full-date">{d.toLocaleDateString('he-IL', { month: 'short' })}</span>
              )}
              {holiday && (
                <span className={`day-holiday-badge ${holiday.severity}`} title={holiday.name}>
                  {holiday.name}
                </span>
              )}
              <AbsenceHeaderBadge
                absences={absences}
                dateStr={formatLocalDateString(d)}
                showName={showTherapistName}
              />
            </div>
          );
        })}
      </div>

      <div className="week-view-body" ref={bodyRef}>
        <div className="time-column">
          {HOURS.map((hour) => (
            <div key={hour} className="time-label">{formatHour(hour)}</div>
          ))}
        </div>

        <div className="days-grid">
          {displayDays.map((dayDate, displayIndex) => {
            const actualDayIndex = singleDayMode ? selectedDayIndex : displayIndex;
            const dayAppts = positionedAppointmentsByDay[actualDayIndex] || [];
            const dayDateStr = formatLocalDateString(dayDate);
            const dayHoliday = getHolidayForDate(dayDate);

            return (
              <div key={displayIndex} className={`day-column ${isDayCollapsed(actualDayIndex, dayDate) ? "collapsed" : ""}`}>
                {HOURS.map((hour) => {
                  const isAvailable = isAvailableHour(dayDate, hour);
                  const isBlocked = isBlockedHour(dayDate, hour);
                  const isPast = isPastSlot(dayDate, hour);
                  const availableTherapists = getTherapistsAvailableAtHour(dayDate, hour);
                  const isDropTarget = dropIndicator?.dateStr === dayDateStr && dropIndicator?.hour === hour;
                  const holidaySeverity = getHolidaySlotSeverity(dayHoliday, hour);
                  const roomHourState = getRoomHourState(dayDate, hour);

                  return (
                    <div
                      key={hour}
                      className={`hour-slot ${isAvailable ? 'available-slot' : ''} ${isBlocked ? 'blocked-slot' : ''} ${isPast ? 'past-slot' : ''} ${isDropTarget ? 'drop-target' : ''} ${holidaySeverity ? `holiday-slot-${holidaySeverity}` : ''} ${roomHourState ? `room-${roomHourState}-slot` : ''}`}
                      data-hour={hour}
                      data-date={dayDateStr}
                      onClick={() => !isPast && !draggedAptId && handleSlotClick(displayIndex, hour)}
                      onContextMenu={(e) => !isPast && handleContextMenu(e, dayDate, hour)}
                      onDragOver={(e) => !isPast && handleSlotDragOver(e, dayDateStr, hour)}
                      onDragLeave={handleSlotDragLeave}
                      onDrop={(e) => !isPast && handleSlotDrop(e, dayDate, hour)}
                      data-tooltip={!draggedAptId ? (isPast ? 'זמן זה כבר עבר' : `${getSlotTooltip(dayDate, hour)}${isBlocked ? ' | זמן חסום' : ''}`) : undefined}
                    >
                      {availableTherapists.length > 0 && (
                        <div className="therapist-availability-lines">
                          {availableTherapists.map((ta, idx) => (
                            <div
                              key={ta.therapistId}
                              className="therapist-line"
                              style={{ backgroundColor: ta.color, right: `${idx * 20}px` }}
                              title={`${ta.name} זמין/ה`}
                            />
                          ))}
                        </div>
                      )}
                      {isDropTarget && (
                        <div className={`drop-indicator ${dropIndicator.isBottomHalf ? 'bottom-half' : 'top-half'}`}>
                          <span className="drop-time-badge">
                            {`${hour.toString().padStart(2, '0')}:${dropIndicator.isBottomHalf ? '30' : '00'}`}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                <AbsenceBands
                  absences={absences}
                  dateStr={dayDateStr}
                  firstHour={FIRST_HOUR}
                  slotHeight={slotHeight}
                  hoursCount={HOURS.length}
                  showName={showTherapistName}
                />
                <div className="appointments-overlay">
                  {dayAppts.map((apt) => (
                    <div
                      key={apt.id}
                      className={`positioned-appointment ${draggedAptId === apt.id ? 'is-dragging' : ''}`}
                      style={{
                        top: `${apt.topPx}px`,
                        height: `${apt.heightPx}px`,
                        ...(apt.overlap.totalCols > 1 ? {
                          left: `${(apt.overlap.col / apt.overlap.totalCols) * 100}%`,
                          right: 'auto',
                          width: `calc(${(100 / apt.overlap.totalCols).toFixed(2)}% - 1px)`,
                        } : {}),
                      }}
                      draggable={canDrag(apt)}
                      onDragStart={(e) => handleDragStart(e, apt)}
                      onDragEnd={handleDragEnd}
                      onTouchStart={(e) => handleTouchStart(e, apt)}
                    >
                      <AppointmentCard
                        appointment={apt}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!draggedAptId) onAppointmentClick(apt);
                        }}
                        onContextMenu={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          onAppointmentContextMenu?.(e, apt);
                        }}
                        compact={true}
                        slotHeight={slotHeight}
                        showTherapistName={showTherapistName}
                        multilineTherapist={multilineTherapist}
                        therapistColor={therapistColorMap?.[apt.therapistId]?.color || null}
                        colorMode={colorMode}
                        treatmentTypeMap={treatmentTypeMap}
                        dimmed={!matchesConfirmationFilter(apt, confirmationFilter)}
                      />
                    </div>
                  ))}
                </div>

                {dayDate.toDateString() === new Date().toDateString() && (
                  <CurrentTimeLine
                    firstHour={FIRST_HOUR}
                    lastHour={FIRST_HOUR + HOURS.length}
                    slotHeight={slotHeight}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {contextMenu && (
        <div
          ref={contextMenuRef}
          className="calendar-context-menu"
          style={{ position: 'fixed', top: contextMenu.y, left: contextMenu.x }}
        >
          <div className="context-menu-date-header">
            {contextMenu.dayDate.toLocaleDateString('he-IL', { weekday: 'long', day: 'numeric', month: 'long' })}
            {' • '}
            {contextMenu.hour.toString().padStart(2, '0')}:00
          </div>
          <button className="context-menu-item" onClick={() => handleMenuAction('create-appointment')}>
            <CalendarPlus size={18} weight="duotone" />
            <span>צור פגישה בשעה זו</span>
          </button>
          <button className="context-menu-item" onClick={() => handleMenuAction('view-day')}>
            <Eye size={18} weight="duotone" />
            <span>צפה בתצוגה יומית</span>
          </button>
          <div className="context-menu-divider" />
          <button className="context-menu-item" onClick={() => handleMenuAction('add-task')}>
            <ListChecks size={18} weight="duotone" />
            <span>הוסף משימה בתאריך זה</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default WeekView;
